import { Dialog, DialogContent, DialogHeading } from 'omakase';
import { Head } from 'head/Head';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { PropsToPass } from 'routes/business/SearchModalRoute';
import { RouteComponentProps } from 'react-router-dom';
import { SeparatedOfferings, separatedOfferingsSelector } from 'selectors/calendarSelectors';
import { forEach, isUndefined } from 'es-toolkit/compat';
import { getNextRelease } from 'utils/businessUtils';
import { getTitleSuffix } from 'utils/seo';
import { oc } from 'ts-optchain';
import { stringify } from '@tock/query-string';
import BallPulse from '@tock/shared/components/BallPulse';
import BaseComponent from '@tock/shared/components/BaseComponent';
import Offerings from 'components/business/offering/Offerings';
import OfferingsStatusMessage from 'components/business/offering/OfferingsStatusMessage';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface ConnectProps extends DefaultConnectProps {
  calendar: StoreType['calendar'];
  lock: StoreType['lock'];
  ui: StoreType['ui'];
  queryParam: StoreType['queryParam'];
  separatedOfferings: SeparatedOfferings;
}

export interface MatchParams {
  widget: string;
  businessId: string;
}

type AllProps = PropsToPass & ConnectProps & RouteComponentProps<MatchParams> & IWithContextProps;
class ExperienceListRoute extends BaseComponent<AllProps> {
  handleShowWaitlist(experienceId: number) {
    const {
      history,
      match: { params },
    } = this.props;
    const prefix = !isUndefined(params.widget) ? '/widget' : '';
    if (experienceId) {
      history.push({
        pathname: `${params.businessId}${prefix}/waitlist?experienceId=${experienceId}`,
        search: stringify({ experienceId }),
      });
    } else {
      history.push(`${prefix}/waitlist`);
    }
  }

  handleClearPackageLocks() {
    const { packageLock } = this.props.lock;
    forEach(oc(packageLock).locks(), (lock) => {
      this.props.actions.lock.unlockTickets({
        ticketSubsetId: lock.ticketSubset?.id,
        ticketGroupId: lock.ticketSubset?.ticketGroupId,
      });
    });
    this.props.actions.lock.clearPackageLock();
  }

  render() {
    const {
      app: { config },
      calendar: { calendar, requestInProgress, offeringsInProgress },
      isWidget,
      separatedOfferings,
      onClose,
    } = this.props;

    const business = config?.business;

    const isDisabled = !calendar || !separatedOfferings;
    const isLoading = requestInProgress || offeringsInProgress;
    const nextRelease = config && getNextRelease(config);

    return (
      <>
        <Head title={`${business?.name} - Experiences${getTitleSuffix(business)}`} />
        <Dialog open={true} onClose={onClose}>
          <DialogHeading onClose={onClose}>{business?.name ?? ''}</DialogHeading>
          <DialogContent disablePadding>
            {isLoading && (
              <div className="ProfileBody-contentLoading">
                <BallPulse />
              </div>
            )}

            {!isLoading && (
              <div>
                {isDisabled && (
                  <div className="SearchModal-body">
                    {business?.name && (
                      <OfferingsStatusMessage
                        businessName={business?.name}
                        nextRelease={nextRelease || undefined}
                      />
                    )}
                  </div>
                )}
                {!isDisabled && (
                  <div className="ReservationList--widget">
                    {business && (
                      <Offerings
                        business={business}
                        nextRelease={nextRelease || undefined}
                        onAddTicketTypeToWaitlist={this.handleShowWaitlist}
                        separatedOfferings={separatedOfferings}
                        ticketGroups={calendar.ticketGroup || []}
                        isWidget={isWidget}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }
}

function mapStateToProps(state: StoreType) {
  return {
    calendar: state.calendar,
    ui: state.ui,
    lock: state.lock,
    queryParam: state.queryParam,
    separatedOfferings: separatedOfferingsSelector(state),
  };
}

export default connect<PropsToPass & RouteComponentProps<MatchParams>, ConnectProps>(
  withContext(ExperienceListRoute),
  mapStateToProps
);
